import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiMoreVertical, FiShare2 } from 'react-icons/fi';
import { usePnl } from '../../hooks/usePnl';
import { Loader } from '../Common/Loader';
import { buildLeaderboardDemoPnlPayload, resolveViewStatsDemoProfile } from '../../data/mosesLeaderboardPnlData';
import { formatSignedUsd, formatUsdIntl } from '../../utils/formatUsd';
import SharePnlReportModal, { PnlSharePayload } from './SharePnlReportModal';

const weekdayHeadersSunFirst = [
    { full: 'SUN', short: 'S' },
    { full: 'MON', short: 'M' },
    { full: 'TUE', short: 'T' },
    { full: 'WED', short: 'W' },
    { full: 'THU', short: 'T' },
    { full: 'FRI', short: 'F' },
    { full: 'SAT', short: 'S' },
];

interface ProfitLossProps {
    user?: any;
    selectedBrokerId: string | null;
    masterUserId?: string | null;
    variant?: 'full' | 'leaderboard';
    leaderboardTraderName?: string | null;
}

interface DailyPerformance {
    date: string;
    day: number;
    pnl: number;
    tradesCount: number;
}

interface PnlStatistics {
    winRate: number;
    winLossRatio: string;
    totalTrades: number;
    bestDay: number;
    worstDay: number;
    bestTrade: number;
    worstTrade: number;
}

interface PnlData {
    success: boolean;
    monthlyNetTotal: number;
    annualNetTotal: number;
    totalPnl?: number;
    dailyPerformance: DailyPerformance[];
    statistics: PnlStatistics;
}

const emptyPnlData = (): PnlData => ({
    success: true,
    monthlyNetTotal: 0,
    annualNetTotal: 0,
    totalPnl: 0,
    dailyPerformance: [],
    statistics: {
        winRate: 0,
        winLossRatio: '0W / 0L',
        totalTrades: 0,
        bestDay: 0,
        worstDay: 0,
        bestTrade: 0,
        worstTrade: 0,
    },
});

const ProfitLoss: React.FC<ProfitLossProps> = ({
    user: _user,
    selectedBrokerId,
    masterUserId,
    variant = 'full',
    leaderboardTraderName = null,
}) => {
    const isLeaderboardVariant = variant === 'leaderboard';
    const viewStatsProfile = isLeaderboardVariant
        ? resolveViewStatsDemoProfile(leaderboardTraderName)
        : null;
    const leaderboardViewStatsDemo = viewStatsProfile !== null;

    const [currentDate, setCurrentDate] = useState(() => new Date());
    const [sharePayload, setSharePayload] = useState<PnlSharePayload | null>(null);
    const [shareMenuOpen, setShareMenuOpen] = useState(false);
    const shareMenuRef = useRef<HTMLDivElement>(null);
    const month = currentDate.getMonth() + 1; 
    const year = currentDate.getFullYear();

    useEffect(() => {
        if (!shareMenuOpen) return;
        const onPointerDown = (event: MouseEvent) => {
            if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
                setShareMenuOpen(false);
            }
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setShareMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [shareMenuOpen]);

    const apiEnabled = !leaderboardViewStatsDemo && (masterUserId ? !!masterUserId : true);
    const { data, isLoading, isFetching } = usePnl(month, year, selectedBrokerId, masterUserId, { enabled: apiEnabled });

    const demoLeaderboardPayload = useMemo(() => {
        if (!viewStatsProfile) return null;
        return buildLeaderboardDemoPnlPayload(viewStatsProfile, year, month);
    }, [viewStatsProfile, year, month]);

    const pnlData = (leaderboardViewStatsDemo ? demoLeaderboardPayload ?? emptyPnlData() : (data as PnlData | undefined)) as PnlData | undefined;
    const monthTransitionLoading = !leaderboardViewStatsDemo && isFetching && !isLoading;

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const monthName = currentDate.toLocaleString('default', { month: 'long' });
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 0 (Sun) - 6 (Sat)

    const calendarDays = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
        calendarDays.push(null);
    }

    // Days of current month
    for (let i = 1; i <= daysInMonth; i++) {
        calendarDays.push(i);
    }

    const formatCurrency = formatUsdIntl;
    const formatDayPnlCompact = formatSignedUsd;

    // Helper to get stats for a specific day
    const getDayStats = (day: number) => {
        if (!pnlData?.dailyPerformance) return null;
        return pnlData.dailyPerformance.find(d => d.day === day);
    };

    const stats = pnlData?.statistics || {
        winRate: 0,
        winLossRatio: '0W / 0L',
        totalTrades: 0,
        bestDay: 0,
        worstDay: 0,
        bestTrade: 0,
        worstTrade: 0,
    };
    const sidebarTotalPnl: number | null =
        leaderboardViewStatsDemo && viewStatsProfile
            ? demoLeaderboardPayload != null
                ? demoLeaderboardPayload.monthlyNetTotal
                : null
            : (pnlData?.totalPnl ?? pnlData?.monthlyNetTotal ?? 0);

    const annualNetTotal = Number(pnlData?.annualNetTotal ?? 0);
    const monthTradesTotal = useMemo(
        () => (pnlData?.dailyPerformance || []).reduce((sum, d) => sum + (d.tradesCount || 0), 0),
        [pnlData?.dailyPerformance],
    );

    const openDayShare = (day: number, pnl: number, trades: number) => {
        const label = new Date(year, month - 1, day).toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
        setSharePayload({
            scope: 'day',
            title: label,
            subtitle: 'Daily realized P&L performance',
            pnl,
            tradesCount: trades,
        });
    };

    const openMonthShare = () => {
        if (sidebarTotalPnl == null) return;
        setSharePayload({
            scope: 'month',
            title: `${monthName} ${year}`,
            subtitle: 'Monthly realized P&L performance',
            pnl: sidebarTotalPnl,
            tradesCount: stats.totalTrades || monthTradesTotal,
            winLossRatio: stats.winLossRatio,
            winRate: stats.winRate,
        });
    };

    const openYearShare = () => {
        setSharePayload({
            scope: 'year',
            title: String(year),
            subtitle: 'Yearly realized P&L performance',
            pnl: annualNetTotal,
            tradesCount: stats.totalTrades || monthTradesTotal,
            winLossRatio: stats.winLossRatio,
            winRate: stats.winRate,
        });
    };

    if (!leaderboardViewStatsDemo && isLoading && !pnlData) {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="rounded-2xl border border-border/80 bg-gradient-to-br from-surface to-surface-elevated/30 p-5 shadow-sm">
                    <div className="flex items-center justify-center gap-3 sm:gap-6">
                        <div className="h-10 w-10 shrink-0 rounded-full bg-muted/30 animate-pulse" />
                        <div className="h-8 w-full max-w-[12rem] rounded-lg bg-muted/30 animate-pulse" />
                        <div className="h-10 w-10 rounded-full bg-muted/30 animate-pulse" />
                    </div>
                </div>
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                    <div className="w-full min-w-0 shrink-0 rounded-2xl border border-border/80 bg-surface min-h-[320px] flex items-center justify-center lg:min-h-[280px] lg:w-[calc((100%-1.5rem)*3/4)]">
                        <Loader fullScreen={false} message="Loading calendar…" />
                    </div>
                    <div className="w-full min-w-0 shrink-0 rounded-2xl border border-border/80 bg-surface p-6 space-y-4 min-h-[280px] lg:min-h-0 lg:w-[calc((100%-1.5rem)/4)]">
                        <div className="h-6 w-4/5 max-w-[200px] rounded bg-muted/30 animate-pulse" />
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-24 rounded-xl bg-muted/20 animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fade-in">
            {/* Month navigation */}
            <div className="rounded-2xl border border-border/80 bg-gradient-to-br from-surface via-surface to-primary-500/[0.03] p-3 sm:p-4 shadow-sm">
                <div className="flex items-center justify-center gap-3 sm:gap-8">
                    <button
                        type="button"
                        onClick={prevMonth}
                        disabled={monthTransitionLoading}
                        aria-label="Previous month"
                        className="group flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-surface-elevated/50 text-muted shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <FiChevronLeft className="h-5 w-5 transition-transform group-hover:-translate-x-0.5" />
                    </button>
                    <div className="min-w-[140px] sm:min-w-[190px] text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted">Performance</p>
                        <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-foreground">
                            {monthName} <span className="text-primary">{year}</span>
                        </h2>
                        {monthTransitionLoading && (
                            <p className="mt-1.5 text-xs font-medium text-primary animate-pulse">Loading this month…</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={nextMonth}
                        disabled={monthTransitionLoading}
                        aria-label="Next month"
                        className="group flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-surface-elevated/50 text-muted shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <FiChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                    </button>
                </div>
            </div>

            <div className="relative flex flex-col gap-3 sm:gap-4 xl:flex-row xl:items-start">
                {monthTransitionLoading && (
                    <div
                        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-2xl bg-background/75 backdrop-blur-[2px] transition-opacity duration-200"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <div className="rounded-full border-2 border-primary/30 border-t-primary h-12 w-12 animate-spin" />
                        <p className="text-sm font-semibold text-foreground">Loading {monthName} {year}</p>
                        <p className="text-xs text-muted">Updating daily P&amp;L and statistics</p>
                    </div>
                )}

                {/* Calendar Column */}
                <div
                    className={`w-full min-w-0 shrink-0 overflow-hidden rounded-2xl border border-border/80 bg-surface shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06] transition-opacity duration-200 xl:w-[calc((100%-1rem)*3/4)] ${monthTransitionLoading ? 'opacity-60' : 'opacity-100'}`}
                >
                    {/* Calendar Header */}
                    <div className="grid grid-cols-7 border-b border-border/80 bg-gradient-to-b from-surface-elevated/80 to-surface-elevated/40">
                        {weekdayHeadersSunFirst.map((day) => (
                            <div key={day.full} className="min-w-0 py-2 text-center text-[8px] sm:text-[10px] font-bold text-muted uppercase tracking-[0.08em]">
                                <span className="sm:hidden">{day.short}</span>
                                <span className="hidden sm:inline">{day.full}</span>
                            </div>
                        ))}
                    </div>

                    {/* Calendar grid — SUN-first month; empty cells when no P/L for that day */}
                    <div className="grid grid-cols-7 auto-rows-[minmax(72px,auto)] sm:auto-rows-[82px] md:auto-rows-[90px]">
                        {calendarDays.map((day, index) => {
                                  if (day === null) {
                                      return (
                                          <div key={`empty-${index}`} className="min-w-0 border-b border-r border-border/40 bg-muted/5" />
                                      );
                                  }

                                  const dayState = getDayStats(day);
                                  const pnl = dayState?.pnl || 0;
                                  const trades = dayState?.tradesCount || 0;
                                  const hasActivity = trades > 0 || pnl !== 0;

                                  let bgColor = '';
                                  let textColor = '';
                                  let ringClass = '';

                                  if (hasActivity) {
                                      if (pnl > 0) {
                                          bgColor = 'bg-emerald-500/[0.12] dark:bg-emerald-500/15';
                                          textColor = 'text-emerald-600 dark:text-emerald-400';
                                          ringClass = 'ring-1 ring-emerald-500/20';
                                      } else if (pnl < 0) {
                                          bgColor = 'bg-rose-500/[0.12] dark:bg-rose-500/15';
                                          textColor = 'text-rose-600 dark:text-rose-400';
                                          ringClass = 'ring-1 ring-rose-500/20';
                                      } else {
                                          bgColor = 'bg-surface-elevated/80';
                                          textColor = 'text-muted';
                                      }
                                  }

                                  return (
                                      <div
                                          key={day}
                                          className={`min-w-0 max-w-full overflow-hidden border-b border-r border-border/40 p-1 sm:p-1.5 flex flex-col items-center justify-center relative transition-all duration-150 hover:bg-muted/10 ${bgColor} ${ringClass}`}
                                      >
                                          <span className="absolute top-1 left-1/2 z-[1] max-w-[calc(100%-4px)] -translate-x-1/2 truncate text-center text-[10px] sm:text-xs font-medium text-muted">
                                              {day}
                                          </span>

                                          {hasActivity && (
                                              <div className="mt-4 w-full min-w-0 max-w-full px-0.5 text-center">
                                                  <div
                                                      className={`font-bold tabular-nums leading-tight break-words ${textColor} text-[9px] sm:text-xs md:text-sm`}
                                                  >
                                                      <span className="sm:hidden">
                                                          {formatDayPnlCompact(pnl)}
                                                      </span>
                                                      <span className="hidden sm:inline">
                                                          {formatSignedUsd(pnl)}
                                                      </span>
                                                  </div>
                                                  <div className="mt-0.5 text-[7px] leading-tight text-muted opacity-90 sm:text-[9px]">
                                                      <span className="line-clamp-2 break-words">
                                                          {trades} trade{trades !== 1 ? 's' : ''}
                                                      </span>
                                                  </div>
                                                  <button
                                                      type="button"
                                                      onClick={(event) => {
                                                          event.stopPropagation();
                                                          openDayShare(day, pnl, trades);
                                                      }}
                                                      title="Share day P&L"
                                                      aria-label={`Share P&L for day ${day}`}
                                                      className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold shadow-sm transition sm:text-[10px] ${
                                                          pnl >= 0
                                                              ? 'bg-green-500/15 text-green-500 ring-1 ring-green-500/30 hover:bg-green-500 hover:text-white'
                                                              : 'bg-red-500/15 text-red-500 ring-1 ring-red-500/30 hover:bg-red-500 hover:text-white'
                                                      }`}
                                                  >
                                                      <FiShare2 className="h-3 w-3" />
                                                      <span className="hidden sm:inline">Share</span>
                                                  </button>
                                              </div>
                                          )}
                                      </div>
                                  );
                              })}
                    </div>
                </div>

                {/* Statistics Column */}
                <div
                    className={`w-full min-w-0 shrink-0 rounded-2xl border border-border/80 bg-gradient-to-b from-surface to-surface-elevated/20 p-4 sm:p-6 h-fit shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06] transition-opacity duration-200 xl:w-[calc((100%-1rem)/4)] ${monthTransitionLoading ? 'opacity-60' : 'opacity-100'}`}
                >
                    <div className="mb-6 flex items-start justify-between gap-3">
                        <div>
                            <h3 className="font-bold text-lg mb-1 text-foreground">{monthName} {year}</h3>
                            <p className="text-xs text-muted">Month summary</p>
                        </div>
                        <div className="relative shrink-0" ref={shareMenuRef}>
                            <button
                                type="button"
                                onClick={() => setShareMenuOpen((open) => !open)}
                                title="Share options"
                                aria-label="Share options"
                                aria-expanded={shareMenuOpen}
                                aria-haspopup="menu"
                                className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border shadow-sm transition ${
                                    sidebarTotalPnl == null
                                        ? 'border-border bg-surface-elevated text-muted'
                                        : sidebarTotalPnl >= 0
                                          ? 'border-green-500/30 bg-green-500/15 text-green-500 hover:bg-green-500 hover:text-white'
                                          : 'border-red-500/30 bg-red-500/15 text-red-500 hover:bg-red-500 hover:text-white'
                                }`}
                            >
                                <FiMoreVertical className="h-4 w-4" />
                            </button>
                            {shareMenuOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-border bg-surface shadow-xl ring-1 ring-black/5 dark:ring-white/10"
                                >
                                    <button
                                        type="button"
                                        role="menuitem"
                                        disabled={sidebarTotalPnl == null}
                                        onClick={() => {
                                            setShareMenuOpen(false);
                                            openMonthShare();
                                        }}
                                        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-foreground transition hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <FiShare2 className="h-3.5 w-3.5 text-primary" />
                                        Share Month
                                    </button>
                                    <div className="h-px bg-border/80" />
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setShareMenuOpen(false);
                                            openYearShare();
                                        }}
                                        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-foreground transition hover:bg-surface-elevated"
                                    >
                                        <FiShare2 className="h-3.5 w-3.5 text-primary" />
                                        Share Year
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Total PnL Card */}
                        <div className="bg-surface-elevated rounded-xl p-4 shadow-sm text-center">
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider mb-2">Total P&L</p>
                            {sidebarTotalPnl === null ? (
                                <div className="text-3xl font-bold tabular-nums text-muted">—</div>
                            ) : (
                                <div
                                    className={`text-2xl sm:text-3xl font-bold tabular-nums break-anywhere ${sidebarTotalPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}
                                >
                                    {formatSignedUsd(sidebarTotalPnl)}
                                </div>
                            )}
                            <div className="text-xs text-muted mt-1">{monthName}</div>
                        </div>

                        {/* Win Rate Card */}
                        <div className="bg-surface-elevated rounded-xl p-4 shadow-sm">
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider mb-2">WIN RATE</p>
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-2xl sm:text-3xl font-bold text-primary">{stats.winRate.toFixed(1)}%</div>
                                    <div className="text-sm text-muted mt-1">{stats.winLossRatio}</div>
                                </div>
                                {/* Simple Circle Chart SVG */}
                                <div className="relative h-16 w-16 shrink-0">
                                    <svg className="h-full w-full rotate-[-90deg]" viewBox="0 0 36 36">
                                        <path
                                            className="text-gray-200"
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3.8"
                                        />
                                        <path
                                            className="text-primary"
                                            strokeDasharray={`${stats.winRate}, 100`}
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3.8"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Total Trades Card */}
                        <div className="bg-surface-elevated rounded-xl p-4 shadow-sm text-center">
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider mb-2">TOTAL TRADES</p>
                            <div className="text-3xl sm:text-4xl font-bold text-primary">{stats.totalTrades}</div>
                            <div className="text-xs text-muted mt-1">{monthName}</div>
                        </div>

                        {!isLeaderboardVariant && (
                            <>
                                {/* Daily Performance */}
                                <div className="bg-surface-elevated rounded-xl p-4 shadow-sm">
                                    <p className="text-xs text-muted font-semibold uppercase tracking-wider mb-4">DAILY PERFORMANCE</p>

                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-muted">Best</span>
                                                <span className="font-bold text-green-500">
                                                    {formatSignedUsd(Math.max(0, stats.bestDay))}
                                                </span>
                                            </div>
                                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-green-500" style={{ width: '100%' }}></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-muted">Worst</span>
                                                <span className="font-bold text-red-500">{formatSignedUsd(Math.min(0, stats.worstDay))}</span>
                                            </div>
                                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-red-500" style={{ width: stats.bestDay !== 0 ? `${(Math.abs(Math.min(0, stats.worstDay)) / Math.abs(stats.bestDay || 1)) * 100}%` : '0%' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Trade Performance (Best/Worst Individual Trade) */}
                                <div className="bg-surface-elevated rounded-xl p-4 shadow-sm">
                                    <p className="text-xs text-muted font-semibold uppercase tracking-wider mb-4">TRADE PERFORMANCE</p>

                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-muted">Best</span>
                                                <span className="font-bold text-green-500">
                                                    {formatSignedUsd(Math.max(0, stats.bestTrade))}
                                                </span>
                                            </div>
                                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-green-500" style={{ width: '100%' }}></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-muted">Worst</span>
                                                <span className="font-bold text-red-500">{formatSignedUsd(Math.min(0, stats.worstTrade))}</span>
                                            </div>
                                            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-red-500" style={{ width: stats.bestTrade !== 0 ? `${(Math.abs(Math.min(0, stats.worstTrade)) / Math.abs(stats.bestTrade || 1)) * 100}%` : '0%' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {sharePayload && (
                <SharePnlReportModal
                    payload={sharePayload}
                    onClose={() => setSharePayload(null)}
                />
            )}
        </div>
    );
};
export default ProfitLoss;
